import { useCallback, useEffect, useMemo, useState } from 'react';
import { Sort } from '@models';
import { defaultSort } from '@core/utils/array';
import { includesInLC } from "@datassential/platform-ui-lib";

export function getFilterSort<ItemType>({
    data: rawData,
    query = '',
    searchKeys,
    sortingKey,
    sortingDirection,
}: {
    data: ItemType[];
    query?: string;
    searchKeys?: keyof ItemType | Array<keyof ItemType>;
    sortingKey: keyof ItemType;
    sortingDirection: Sort;
}): ItemType[] {
    const filteredData = [...(rawData || [])].filter(item => {
        if (query.trim() !== '') {
            if (Array.isArray(searchKeys)) {
                return searchKeys.some(key => includesInLC(`${item[key]}`, query.trim()));
            } else if (typeof searchKeys === 'string') {
                return includesInLC(`${item[searchKeys]}`, query.trim());
            }
        }

        return true;
    });

    const result = [...filteredData];

    result.sort((a, b) => defaultSort(sortingDirection)(a[sortingKey], b[sortingKey]));

    return result;
}


export function useFilterSort<ItemType>(
    rawData: ItemType[],
    initialSortingKey: keyof ItemType,
    initialSortingDirection: Sort = Sort.Desc,
    searchKeys?: keyof ItemType | Array<keyof ItemType>,
) {
    const [query, setQuery] = useState('');
    const [key, setKey] = useState<keyof ItemType>(initialSortingKey);
    const [direction, setDirection] = useState<Sort>(initialSortingDirection);

    const data = useMemo(
        () => getFilterSort({
            data: rawData,
            query,
            searchKeys,
            sortingKey: key,
            sortingDirection: direction,
        }),
        [rawData, query, searchKeys, key, direction]
    );

    useEffect(() => {
        setKey(initialSortingKey);
        setDirection(initialSortingDirection);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSortingKey]);

    const sort = useCallback(
        (rawData: ItemType[]) => getFilterSort({
            data: rawData,
            query,
            searchKeys,
            sortingKey: key,
            sortingDirection: direction,
        }),
        [query, searchKeys, key, direction]
    );

    return {
        data,
        sortingKey: key,
        setSortingKey: setKey,
        sortingDirection: direction,
        setSortingDirection: setDirection,
        query,
        setQuery,
        sort,
    };
}